// Backend API client.
//
// All HTTP calls go through this module so the backend URL lives in exactly
// one place — the runtime config — and individual call sites do not have to
// stitch together URLs.
//
// The backend uses an HttpOnly session cookie for auth; every request must
// include credentials so the browser attaches it. We never read or write
// tokens from JS — the session id is opaque and lives only in the cookie.

import { ensureRuntimeConfig } from "./runtime-config.js";
import { DEFAULT_WORKFLOW_ID } from "./constants.js";

function joinUrl(base, path) {
  // ``base`` is normalised to have no trailing slash; ``path`` is allowed to
  // start with one or not. ``new URL`` would resolve relative paths against
  // the document, which is not what we want for a configurable API origin.
  const cleanBase = base.endsWith("/") ? base.slice(0, -1) : base;
  const cleanPath = path.startsWith("/") ? path : `/${path}`;
  return `${cleanBase}${cleanPath}`;
}

export class UnauthorizedError extends Error {
  constructor(url) {
    super(`unauthorized: ${url}`);
    this.name = "UnauthorizedError";
    this.status = 401;
    this.url = url;
  }
}

export class ApiError extends Error {
  constructor(url, status, body) {
    super(`${url} returned ${status}`);
    this.name = "ApiError";
    this.status = status;
    this.url = url;
    this.body = body;
  }
}

async function fetchJson(url, opts = {}) {
  const res = await fetch(url, {
    method: "GET",
    // Cookie auth: include credentials on every request. Same-origin and
    // explicit-CORS-allowlist requests both rely on this.
    credentials: "include",
    cache: "no-store",
    ...opts,
  });
  if (res.status === 401) {
    throw new UnauthorizedError(url);
  }
  if (!res.ok) {
    let body = null;
    try {
      body = await res.json();
    } catch (_) {
      body = null;
    }
    throw new ApiError(url, res.status, body);
  }
  // 204 No Content shouldn't have a body.
  if (res.status === 204) return null;
  return res.json();
}

export async function getConfig() {
  const cfg = await ensureRuntimeConfig();
  return fetchJson(joinUrl(cfg.apiBaseUrl, "/config"));
}

function _withLangQuery(path, lang) {
  if (!lang) return path;
  const safe = encodeURIComponent(String(lang));
  return path.includes("?") ? `${path}&lang=${safe}` : `${path}?lang=${safe}`;
}

export async function listWorkflows({ lang } = {}) {
  const cfg = await ensureRuntimeConfig();
  const body = await fetchJson(
    joinUrl(cfg.apiBaseUrl, _withLangQuery("/workflows", lang)),
  );
  return Array.isArray(body.workflows) ? body.workflows : [];
}

export async function getWorkflow(workflowId, { lang } = {}) {
  const cfg = await ensureRuntimeConfig();
  const id = workflowId || DEFAULT_WORKFLOW_ID;
  return fetchJson(
    joinUrl(cfg.apiBaseUrl, _withLangQuery(`/workflows/${encodeURIComponent(id)}`, lang)),
  );
}

export async function getProtocol() {
  const cfg = await ensureRuntimeConfig();
  return fetchJson(joinUrl(cfg.apiBaseUrl, "/protocol"));
}

// ---- Auth -------------------------------------------------------------------

export async function login({ space, code }) {
  const cfg = await ensureRuntimeConfig();
  return fetchJson(joinUrl(cfg.apiBaseUrl, "/auth/login"), {
    method: "POST",
    headers: { "content-type": "application/json", accept: "application/json" },
    body: JSON.stringify({ space, code }),
  });
}

export async function logout() {
  const cfg = await ensureRuntimeConfig();
  return fetchJson(joinUrl(cfg.apiBaseUrl, "/auth/logout"), {
    method: "POST",
  });
}

export async function getSession() {
  const cfg = await ensureRuntimeConfig();
  return fetchJson(joinUrl(cfg.apiBaseUrl, "/auth/me"));
}

// Pure helpers exposed for tests.
export const _internals = { joinUrl };
